import { useEffect, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { LibraryCategory } from "@/api/models"

import { CheckSquare, ChevronDown, Search, Square, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Switch } from "@/components/ui/switch"
import { api } from "@/lib/api-client"
import { cn } from "@/lib/utils"

import { filterCategoriesByQuery } from "./category-picker-utils"

type CategoryPickerProps = {
  libraryId: string
  activeCategories: string[]
  onCategoryChange: (categories: string[]) => void
  triggerVariant?: "default" | "footer"
  triggerLabel?: string
  popoverSide?: "top" | "bottom" | "left" | "right"
}

export function CategoryPicker({
  libraryId,
  activeCategories,
  onCategoryChange,
  triggerVariant = "default",
  triggerLabel,
  popoverSide,
}: CategoryPickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const isFooterVariant = triggerVariant === "footer"
  const resolvedLabel = triggerLabel ?? t("libraries.select_categories")
  const resolvedPopoverSide = popoverSide ?? (isFooterVariant ? "top" : "bottom")

  // Fetch available categories for this library
  const {
    data: categoriesData,
    isLoading,
    isSuccess,
  } = useQuery<{
    library_id: string
    categories: LibraryCategory[]
  }>({
    queryKey: ["library-categories", libraryId],
    queryFn: async () => {
      return await api.get(`/explorer/libraries/${libraryId}/categories`)
    },
    enabled: !!libraryId,
  })

  const categories = categoriesData?.categories || []

  const validValues = useMemo(() => new Set(categories.map((c) => c.value)), [categories])

  const selectedKnownCount = useMemo(
    () => activeCategories.filter((c) => validValues.has(c)).length,
    [activeCategories, validValues]
  )

  // Drop category strings that no longer exist for this library (stale settings / renames).
  // Only after a successful fetch: on failure, categories is empty and we must not wipe selections.
  useEffect(() => {
    if (!isSuccess) {
      return
    }
    const pruned = activeCategories.filter((c) => validValues.has(c))
    if (pruned.length !== activeCategories.length) {
      onCategoryChange(pruned)
    }
  }, [isSuccess, activeCategories, validValues, onCategoryChange])

  // Filter categories based on search
  const filteredCategories = useMemo(
    () => filterCategoriesByQuery(categories, searchQuery),
    [categories, searchQuery]
  )

  const toggleCategory = (category: string) => {
    const newCategories = activeCategories.includes(category)
      ? activeCategories.filter((c) => c !== category)
      : [...activeCategories, category]

    onCategoryChange(newCategories)
  }

  const enableAll = () => {
    const allCategories = filteredCategories.map((cat) => cat.value)
    const base = activeCategories.filter((c) => validValues.has(c))
    const newCategories = Array.from(new Set([...base, ...allCategories]))
    onCategoryChange(newCategories)
  }

  const disableAll = () => {
    const filteredValues = new Set(filteredCategories.map((cat) => cat.value))
    onCategoryChange(activeCategories.filter((c) => !filteredValues.has(c)))
  }

  const allEnabled =
    filteredCategories.length > 0 &&
    filteredCategories.every((cat) => activeCategories.includes(cat.value))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {isFooterVariant ? (
          <Button
            aria-expanded={open}
            className="text-muted-foreground hover:text-foreground dark:hover:text-accent-foreground h-7 gap-1.5 px-2"
            role="combobox"
            size="md"
            type="button"
            variant="ghost"
            onMouseDown={(event) => {
              event.preventDefault()
            }}
          >
            <span>{resolvedLabel}</span>
            {selectedKnownCount > 0 && (
              <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs">
                {selectedKnownCount}
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        ) : (
          <Button
            aria-expanded={open}
            className="justify-between text-sm"
            role="combobox"
            type="button"
            variant="outline"
          >
            <div className="flex items-center gap-2">
              <span>{resolvedLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              {selectedKnownCount > 0 && (
                <span className="border-foreground bg-primary text-primary-foreground rounded-full border px-2 py-0.5 text-xs">
                  {selectedKnownCount}
                </span>
              )}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[400px] overflow-hidden rounded-lg p-0"
        side={resolvedPopoverSide}
        sideOffset={isFooterVariant ? 8 : 0}
      >
        <div className="flex flex-col gap-2 p-3">
          {/* Search */}
          <div className="relative">
            <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
            <Input
              className="pl-8"
              placeholder={t("libraries.search_categories")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <Button
                className="absolute top-1 right-1 h-7 w-7"
                size="icon"
                variant="ghost"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Category List */}
        <ScrollArea className="h-[300px]">
          {isLoading && (
            <div className="text-muted-foreground p-4 text-center text-sm">Loading...</div>
          )}
          {!isLoading && filteredCategories.length === 0 && (
            <div className="text-muted-foreground p-4 text-center text-sm">
              {searchQuery ? t("libraries.no_matches") : t("libraries.no_categories")}
            </div>
          )}
          {!isLoading && filteredCategories.length > 0 && (
            <div className="flex flex-col gap-1 p-2">
              {filteredCategories.map((cat) => {
                const isActive = activeCategories.includes(cat.value)
                return (
                  <button
                    key={cat.value}
                    className="hover:bg-accent flex items-center gap-3 rounded-md p-2"
                    onClick={() => toggleCategory(cat.value)}
                  >
                    <Button
                      size="icon"
                      variant="ghost"
                      className={cn(
                        "-m-0.5 size-6 shrink-0 p-0",
                        isActive ? "text-primary" : "text-muted-foreground"
                      )}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleCategory(cat.value)
                      }}
                    >
                      {isActive ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </Button>
                    <div className="flex flex-1 items-baseline justify-between">
                      <span className="text-sm">{cat.value}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="flex items-center justify-between border-t p-3">
          <div className="text-muted-foreground text-sm">
            {t("libraries.selected_of_available", {
              selected: selectedKnownCount,
              total: categories.length,
            })}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">{t("libraries.enable_all")}</span>
            <Switch
              checked={allEnabled}
              onCheckedChange={(checked) => (checked ? enableAll() : disableAll())}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
